import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";

export default function NewsletterForm({ compact = false }) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [state, setState] = useState("idle"); // idle | sending | done
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setError("");
    try {
      await api.post("/subscribe", { email, website });
      setState("done");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setState("idle");
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      toast.error(typeof detail === "string" ? detail : "Subscription failed");
    }
  };

  if (state === "done") {
    return (
      <div
        className={`flex items-start gap-3 rounded-xl border border-cyanx/30 bg-fog/70 ${compact ? "p-4" : "p-6"}`}
        data-testid={T.newsletterSuccess}
      >
        <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-cyanx" />
        <p className="text-sm leading-relaxed text-dim">
          Almost there — we've emailed you a confirmation link. Click it to complete the double
          opt-in. Didn't get it? Check spam, then try again.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full" noValidate>
      <div className={`flex flex-col gap-3 sm:flex-row ${compact ? "" : "sm:items-center"}`}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid={T.newsletterEmail}
          className="w-full rounded-full border border-white/10 bg-[#0B101C] px-5 py-3.5 text-sm text-ink placeholder:text-mute2 focus:border-cyanx/60 focus:outline-none focus:ring-1 focus:ring-cyanx/40"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid={T.newsletterSubmit}
          className="group flex shrink-0 items-center justify-center gap-2 rounded-full bg-cyanx px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-void transition-all duration-300 hover:brightness-110 disabled:opacity-60"
        >
          {state === "sending" ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <>
              Join
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </>
          )}
        </button>
      </div>
      {/* honeypot — invisible to humans */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
      />
      {error && (
        <p className="mt-3 text-xs text-ember" data-testid={T.newsletterError}>
          {error}
        </p>
      )}
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mute2">
        Double opt-in · one-click unsubscribe · no spam, ever
      </p>
    </form>
  );
}
